/** Decimal parsing and compatibility shapes for display; scoring stays in the API. */

const numericFields = new Set([
  "value",
  "max_score",
  "final",
  "final_score",
  "total",
  "total_raw",
  "penalty_points",
  "weight",
  "points",
]);
const numericMaps = new Set([
  "scores",
  "finals",
  "attempt_finals",
  "rider_attempt_scores",
  "judge_scores",
  "criteria",
]);

function object(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function decode(value: unknown, key: string, numericMap: boolean): unknown {
  if (key === "criteria_breakdown") return value;
  if (
    typeof value === "string" &&
    (numericMap || numericFields.has(key)) &&
    /^-?\d+(?:\.\d+)?$/.test(value)
  ) {
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
  }
  if (Array.isArray(value)) return value.map((item) => decode(item, "", false));
  if (!object(value)) return value;
  const result: Record<string, unknown> = Object.fromEntries(
    Object.entries(value).map(([name, item]) => [
      name,
      decode(item, name, numericMap || numericMaps.has(name)),
    ]),
  );
  // Projected judge rosters are flat. Adapt their visible label without resolving
  // an underlying rider identity or claiming a photo that the projection omitted.
  const anonymous = typeof result.anon_label === "string";
  const rosterLabel = anonymous ? result.anon_label : result.name;
  if (
    typeof result.id === "string" &&
    !result.rider &&
    typeof rosterLabel === "string" &&
    (anonymous ||
      (typeof result.current_attempt === "number" && typeof result.sort_order === "number"))
  ) {
    result.rider = {
      id: result.id,
      name: rosterLabel,
      nickname: anonymous ? null : (result.nickname ?? null),
      level: null,
      fun_fact: null,
      has_photo: false,
      created_at: "",
      updated_at: "",
    };
    result.colour ??= result.color ?? null;
    result.number ??= result.bib ?? null;
  }
  if (typeof result.heat_rider_id === "string" && "value" in result && !("rider_id" in result)) {
    result.rider_id = result.heat_rider_id;
  }
  if (
    Array.isArray(result.competitors) &&
    (result.type === "scores_update" || result.type === "results_update")
  ) {
    const finals: Record<string, unknown> = {};
    const scores: Record<string, unknown> = {};
    const attempts: Record<string, Record<string, unknown>> = {};
    const attemptFinals: Record<string, Record<string, unknown>> = {};
    for (const row of result.competitors) {
      if (!object(row) || typeof row.heat_rider_id !== "string") continue;
      const id = row.heat_rider_id;
      finals[id] = row.final ?? null;
      attempts[id] = {};
      attemptFinals[id] = {};
      if (!Array.isArray(row.attempts)) continue;
      for (const attempt of row.attempts) {
        if (!object(attempt)) continue;
        const number = String(attempt.attempt_number);
        if (object(attempt.judge_scores)) {
          attempts[id][number] = attempt.judge_scores;
          scores[id] = attempt.judge_scores;
        }
        if ("total" in attempt) attemptFinals[id][number] = attempt.total;
      }
    }
    Object.assign(result, {
      type: "scores_update",
      finals,
      scores,
      ...(result.version === 2 && object(result.rider_attempt_scores)
        ? { legacy_rider_attempt_scores: attempts }
        : { rider_attempt_scores: attempts }),
      attempt_finals: attemptFinals,
    });
  }
  return result;
}

export function decodeWire(value: unknown): unknown {
  return decode(value, "", false);
}
